// Central place for tunable business rules and public contact details.
// NEXT_PUBLIC_* values are safe to read on the client.

export const CASH_DEPOSIT_AMD = Number(
  process.env.NEXT_PUBLIC_CASH_DEPOSIT_AMD ?? 10000,
);

export const LAST_MINUTE_DISCOUNT_PERCENT = Number(
  process.env.NEXT_PUBLIC_LAST_MINUTE_DISCOUNT_PERCENT ?? 15,
);

export const LAST_MINUTE_WINDOW_HOURS = Number(
  process.env.NEXT_PUBLIC_LAST_MINUTE_WINDOW_HOURS ?? 48,
);

// Emails that should automatically receive the ADMIN role (comma-separated
// in ADMIN_EMAILS). Use this to make yourself an admin on first sign-in.
const adminEmails = (process.env.ADMIN_EMAILS ?? "")
  .split(",")
  .map((e) => e.trim().toLowerCase())
  .filter(Boolean);

export function isAdminEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  return adminEmails.includes(email.toLowerCase());
}

export type Contact = {
  phone: string;
  whatsapp: string;
  email: string;
  instagram: string;
  facebook: string;
};

/**
 * Contact details, read at runtime so hosting env changes take effect after a
 * restart (no rebuild). Prefers the plain CONTACT_* vars; falls back to the old
 * NEXT_PUBLIC_* names (inlined at build time) and then to defaults.
 *
 * Call this from server components only — it reads server env at request time.
 */
export function getContact(): Contact {
  return {
    phone:
      process.env.CONTACT_PHONE ??
      process.env.NEXT_PUBLIC_PHONE ??
      "+374 00 000000",
    whatsapp:
      process.env.CONTACT_WHATSAPP ??
      process.env.NEXT_PUBLIC_WHATSAPP ??
      "37400000000",
    email:
      process.env.CONTACT_EMAIL ??
      process.env.NEXT_PUBLIC_CONTACT_EMAIL ??
      "hello@welcomehouse.am",
    instagram:
      process.env.CONTACT_INSTAGRAM ?? process.env.NEXT_PUBLIC_INSTAGRAM ?? "",
    facebook:
      process.env.CONTACT_FACEBOOK ?? process.env.NEXT_PUBLIC_FACEBOOK ?? "",
  };
}

// Formats an integer AMD amount like "45,000 ֏" / "45,000".
export function formatAmd(amount: number): string {
  return new Intl.NumberFormat("en-US").format(Math.round(amount));
}

// Compact AMD for tight spaces (calendar cells): 45000 → "45K", 27500 → "27.5K".
export function compactAmd(amount: number): string {
  if (amount < 1000) return String(Math.round(amount));
  const k = amount / 1000;
  return `${Number.isInteger(k) ? k : k.toFixed(1)}K`;
}
